// @ts-check

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import lodash from 'lodash';
import axiosClient from 'api/axiosClient';
import { transformedArray } from 'helpers';
import { useRouter } from 'next/router';

const DEFAULT_OMIT_QUERY = [
  'page',
  'rowPerPage',
  'textSearch',
  'order',
  'orderBy',
  'courseCentre',
];

/**
 * Custom hook to fetch data using Axios with support for queryFn, transform, and deep dependency tracking.
 *
 * @template DataT
 * @template TransformT
 * @template ResponseT
 * @param {Object} params
 * @param {import('axios').AxiosRequestConfig & {query?: Record<string, any>}} [params.options={}] - Axios config
 * @param {DataT} [params.defaultValue] - Default state value
 * @param {(options: import('axios').AxiosRequestConfig & {query?: Record<string, any>, advanceFilter?: Array<{ field: string, type: string, value: Array<string | number> }> | {}}) => Promise<ResponseT>} params.queryFn - Custom fetch function
 * @param {(string | number | boolean | Record<string, any>)[]} params.queryKey - Dependency array
 * @param {boolean} [params.enable=true] - Enable fetching
 * @param {(data: ResponseT) => TransformT} [params.transform] - Optional transform function
 * @param {(transformed: TransformT | ResponseT, raw: ResponseT) => void} [params.onSuccess] - Optional success callback
 * @param {{query: Record<string, string | string[]>, omitValue?: string[], mapValues?: Array<[string, (val:  string) => any]>}} [params.filterOption] -
 * @param {boolean} [params.enableFilter=false] - Enable advance filter
 * @returns {{ data: TransformT | ResponseT | DataT, loading: boolean, error: any, refresh: () => void }} - Fetch result
 */
function useFetch({
  options = {},
  defaultValue,
  queryFn,
  queryKey = [],
  enable = true,
  transform,
  onSuccess,
  ...props
}) {
  /**
   * @type {[TransformT | ResponseT | DataT, React.Dispatch<React.SetStateAction<TransformT | ResponseT | DataT>>]}
   */
  const [data, setData] = useState(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const router = useRouter();

  const {
    filterOption = {
      query: { ...router.query },
      omitValue: DEFAULT_OMIT_QUERY,
      mapValues: [],
    },
    enableFilter = false,
  } = props;

  const cancelSourceRef = useRef(null);
  const prevQueryKeyRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    cancelSourceRef.current = axios.CancelToken.source();

    try {
      const cancelToken = cancelSourceRef.current.token;

      let advanceFilter = [];
      if (enableFilter) {
        advanceFilter = transformedArray(
          filterOption.query,
          filterOption.omitValue,
          filterOption.mapValues,
        );
      }

      let result = await queryFn({ ...options, cancelToken, advanceFilter });

      const transformed =
        typeof transform === 'function' ? transform(result) : result;

      if (typeof onSuccess === 'function') {
        onSuccess(transformed, result);
      }
      setData(transformed || result);
    } catch (err) {
      if (!axios.isCancel(err)) {
        setError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!enable) {
      setLoading(false);
      return;
    }
    const hasChanged = lodash.isEqual(prevQueryKeyRef.current, queryKey);

    if (hasChanged) return;

    prevQueryKeyRef.current = JSON.parse(JSON.stringify(queryKey));
    fetchData();

    return () => {
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel(
          'Cancelled due to queryKey change or unmount',
        );
      }
    };
  }, [queryKey, enable]); // Keep external refs minimal, queryKey is deeply compared

  return { data, loading, error, refresh: fetchData };
}

export default useFetch;
